/**
 * @param {TreeNode} nodes - menu structure ({number} id, {string} label, {string} [screen],
 *  {IconName} [icon], {TreeNodeType} children - sub menu )
 * @returns {React.ReactNode}
 */
import React, { useState, useEffect } from 'react';
import { View, TouchableOpacity, StyleSheet, TextStyle } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconName } from '@fortawesome/fontawesome-common-types';
import { solid /*, regular, brands, icon*/ } from '@fortawesome/fontawesome-svg-core/import.macro'; // <-- import styles to be used
import { theme } from '../styles';
import { BLink, BText } from '.';

export interface TreeNode {
  id: number;
  label: string;
  screen?: string;
  icon?: IconName;
  children?: TreeNode[];
}

interface Props {
  nodes: TreeNode[];
}

export const TreeView: React.FC<Props> = ({ nodes }) => {
  const navigation = useNavigation();
  const navigationState = navigation.getState();

  const [collapsedNodes, setCollapsedNodes] = useState<number[]>([]);
  const [selectedNode, setSelectedNode] = useState<string | null>(null);

  useEffect(() => {
    let totalRoutes = navigationState.routes.length;
    let totalStateRoutes = navigationState.routes[totalRoutes - 1].state?.routes.length || 0;
    setSelectedNode(navigationState.routes[totalRoutes - 1].state?.routes[totalStateRoutes - 1].name || '');
    //console.log(navigationState, navigationState.routes[totalRoutes - 1].state?.routes[totalStateRoutes - 1].name || '')
  }, [navigationState]);

  const isNodeCollapsed = (nodeId: number) => {
    return collapsedNodes.includes(nodeId);
  };

  const toggleNodeCollapsed = (nodeId: number) => {
    const isCollapsed = isNodeCollapsed(nodeId);
    if (isCollapsed) {
      setCollapsedNodes(collapsedNodes.filter(id => id !== nodeId));
    } else {
      setCollapsedNodes([...collapsedNodes, nodeId]);
    }
  };

  const renderNode = (node: TreeNode) => {
    const hasChildren = node.children && node.children.length > 0;
    const isCollapsed = hasChildren && isNodeCollapsed(node.id);
    const isSelected = selectedNode === node.screen;

    return (
      <View key={node.id} style={treeViewStyle.container}>
        <TouchableOpacity
          onPress={() => {
            toggleNodeCollapsed(node.id);
          }}
        >
          <View style={treeViewStyle.nodeItem}>
            {hasChildren && (
              <>
                {isCollapsed && <FontAwesomeIcon icon={solid('angle-right')} style={treeViewStyle.nodeItemIcon} />}
                {!isCollapsed && <FontAwesomeIcon icon={solid('angle-down')} style={treeViewStyle.nodeItemIcon} />}
                <BText style={treeViewStyle.nodeItemText}>{node.label}</BText>
              </>
            )}
            {!hasChildren && (
              <BLink to={{ screen: node.screen }} style={nodeItemLink(isSelected)}>
                {node.label}
              </BLink>
            )}
          </View>
        </TouchableOpacity>
        {hasChildren && !isCollapsed && (
          <View style={treeViewStyle.nestedContainer}>
            {node.children!.map(childNode => (
              <TreeView key={childNode.id} nodes={[childNode]} />
            ))}
          </View>
        )}
      </View>
    );
  };

  return <>{nodes.map(node => renderNode(node))}</>;
};

// Styles
const nodeItemLink = (isSelected: boolean): TextStyle => {
  return {
    ...theme.text.menu,
    fontWeight: isSelected ? 'bold' : 'normal',
    flex: 1,
  };
};

const treeViewStyle = StyleSheet.create({
  nestedContainer: {
    marginLeft: 10,
  },
  container: {
    marginLeft: 12,
  },
  nodeItem: {
    flex: 1,
    flexDirection: 'row',
    marginTop: 5,
    marginBottom: 5,
  },
  nodeItemIcon: {
    width: 20,
  },
  nodeItemText: {
    ...theme.text.menu,
  },
});
